import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { useEffect, useRef } from 'react';
import type { DragSourceMonitor } from 'react-dnd';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { recordCarouselControlClickEvent } from './carouselControlClickEvents';
import * as styles from './CarouselThumbnail.module.scss';
import CarouselThumbnailImage from './CarouselThumbnailImage';
import { isUploadedCarouselItem } from './types';
import type { CarouselItem } from './types';
import Button from '../../components/Button';

export const CAROUSEL_ITEM_DRAG_TYPE = 'carousel-item';

const CAROUSEL_DRAG_START_EVENTS = new WeakSet();

/**
 * Quill disallows drag events in the Scroll blot. To bypass this, we record carousel drag start events
 * and allow them in the Scroll blot.
 * This is more reliable than checking `event.target`, as sometimes the target may be a descendant of a
 * `draggable=true` element.
 */
export function isCarouselThumbnailDragStartEvent(event: DragEvent) {
  return CAROUSEL_DRAG_START_EVENTS.has(event);
}

export type ThumbnailDragItem = {
  index: number;
  image: CarouselItem;
  remove(): void;
  isSelected: boolean;
};

export type FileDragItem = {
  index: number;
  files: File[];
  items: DataTransferItemList;
};

interface DragHandlerProps
  extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  onDragEnd: (didDrop: boolean, removed: boolean) => void;
  dragItem: ThumbnailDragItem;
  canDrag: () => boolean;
}

const DragHandler = ({
  onDragEnd,
  canDrag,
  dragItem,
  className,
  ...props
}: DragHandlerProps) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: CAROUSEL_ITEM_DRAG_TYPE,
    canDrag,
    item: (): ThumbnailDragItem => dragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_item, monitor: DragSourceMonitor<unknown, { removed: boolean }>) => {
      onDragEnd(monitor.didDrop(), monitor.getDropResult()?.removed ?? false);
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const imageRef = useRef<HTMLImageElement>(null);
  drag(imageRef);

  return (
    <div
      ref={imageRef}
      className={classNames(className, { [styles.isDragging]: isDragging })}
      onDragStartCapture={({ nativeEvent }) => {
        CAROUSEL_DRAG_START_EVENTS.add(nativeEvent);
      }}
      onDragStart={(e) => {
        if (!canDrag) {
          e.preventDefault();
        }
      }}
      {...props}
    />
  );
};

interface CarouselThumbnailProps {
  image: CarouselItem;
  index: number;
  selected: boolean;
  onSelectedIndexChange: (index: number) => void;
  /**
   * Not passing this prop will disable drag and drop for the thumbnail
   */
  dragControl?: {
    canDrag: () => boolean;
    onDragEnd: (didDrop: boolean, removed: boolean) => void;
    dragItem: ThumbnailDragItem;
  };
}

const CarouselThumbnail = ({
  image,
  index,
  selected,
  onSelectedIndexChange,
  dragControl,
}: CarouselThumbnailProps) => {
  const isUploading = !isUploadedCarouselItem(image);
  const commonProps = {
    className: classNames(styles.container, {
      [styles.isUploading]: isUploading,
    }),
    children: (
      <>
        <CarouselThumbnailImage selected={selected} image={image} />
        {isUploading && <div className={styles.uploadingProgress} />}
      </>
    ),
  };

  return (
    <Button
      className={styles.thumbnailButton}
      role="tab"
      aria-selected={selected}
      onClick={({ nativeEvent }) => {
        recordCarouselControlClickEvent(nativeEvent);
        onSelectedIndexChange(index);
      }}
    >
      {dragControl ? (
        <DragHandler
          {...commonProps}
          canDrag={dragControl.canDrag}
          onDragEnd={dragControl.onDragEnd}
          dragItem={dragControl.dragItem}
        />
      ) : (
        <div {...commonProps} />
      )}
    </Button>
  );
};

export default CarouselThumbnail;
